import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';

const CoverLetterGen = ({ formData, handleInputChange, handleCoverLetterGenerate, isLoading }) => {
  // Simplified: user can paste entire job advert OR provide a URL. Both optional; we'll use one if present.
  const ready = useMemo(() => {
    const hasText = (formData.jobDescription || '').trim().length > 20;
    const hasUrl = (formData.jobPostUrl || '').trim().length > 7;
    return hasText || hasUrl;
  }, [formData.jobDescription, formData.jobPostUrl]);

  const blockedHint = useMemo(() => {
    const url = (formData.jobPostUrl || '').toLowerCase();
    if (!url) return '';
    if (url.includes('indeed.com') || url.includes('linkedin.com')) {
      return 'Note: This site may block automated fetching. If it fails, paste the job text above.';
    }
    return '';
  }, [formData.jobPostUrl]);

  // Collapse job source inputs after a cover letter is generated, unless user is editing
  const jobSourceRef = useRef(null);
  const [expanded, setExpanded] = useState(true);
  const hasCoverLetter = !!(formData.coverLetter && String(formData.coverLetter).trim());
  const [pasteHint, setPasteHint] = useState('');

  // Initialize expansion based on whether a cover letter exists
  useEffect(() => {
    if (isLoading) return; // don't change while loading
    if (hasCoverLetter) {
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  }, [hasCoverLetter, isLoading]);

  const onFocusInside = useCallback(() => setExpanded(true), []);
  const onBlurInside = useCallback(() => {
    // Delay collapse check slightly to allow focus to move within the container
    setTimeout(() => {
      try {
        const active = document.activeElement;
        const container = jobSourceRef.current;
        if (!container) return;
        const stillInside = container.contains(active);
        if (!stillInside && hasCoverLetter && !isLoading) {
          setExpanded(false);
        }
      } catch {}
    }, 80);
  }, [hasCoverLetter, isLoading]);

  const extractFirstUrl = (text) => {
    if (!text) return '';
    const urlRegex = /(https?:\/\/[^\s)>'"\]]+)/i;
    const match = text.match(urlRegex);
    return match ? match[1] : '';
  };

  const handlePasteUrl = useCallback(async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.readText) {
        // Fallback: ask user to paste manually
        setPasteHint('Clipboard not available. Click the URL box and press Ctrl+V.');
        setTimeout(() => setPasteHint(''), 2500);
        return;
      }
      const clip = await navigator.clipboard.readText();
      const candidate = extractFirstUrl(clip) || clip.trim();
      if (!candidate) {
        setPasteHint('Clipboard is empty.');
        setTimeout(() => setPasteHint(''), 1800);
        return;
      }
      handleInputChange({ target: { name: 'jobPostUrl', value: candidate } });
      setPasteHint('URL pasted.');
      setTimeout(() => setPasteHint(''), 1500);
    } catch (err) {
      setPasteHint('Unable to read clipboard.');
      setTimeout(() => setPasteHint(''), 2000);
    }
  }, [handleInputChange]);

  return (
    <div className="cover-letter-generator">
      {/* Collapsible job source fields */}
      <div
        ref={jobSourceRef}
        className={`job-source-collapsible ${expanded ? 'expanded' : 'collapsed'}`}
        onFocus={onFocusInside}
        onBlur={onBlurInside}
      >
        <div className="input-group">
        <label htmlFor="jobDescription">Paste the job advert (or key responsibilities)</label>
        <textarea
          id="jobDescription"
          name="jobDescription"
          value={formData.jobDescription || ''}
          onChange={handleInputChange}
          placeholder="Paste the full job post text here. If left empty, provide the Job Post URL below."
          rows={8}
        />
        {!formData.jobDescription && (formData.jobPostUrl || '').trim() && (
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
            Leave this empty and I’ll auto-fill it from the link below.
          </div>
        )}
        </div>
        <div className="input-group">
          <label htmlFor="jobPostUrl">Or provide the Job Post URL</label>
          <div className="url-input-row">
            <input
              type="text"
              id="jobPostUrl"
              name="jobPostUrl"
              value={formData.jobPostUrl || ''}
              onChange={handleInputChange}
              placeholder="https://…"
              aria-label="Job post URL"
            />
            <button
              type="button"
              className="paste-button"
              onClick={handlePasteUrl}
              disabled={isLoading}
              aria-label="Paste URL from clipboard"
              title="Paste URL from clipboard"
            >
              Paste URL
            </button>
          </div>
          {blockedHint && (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>{blockedHint}</div>
          )}
          {pasteHint && (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>{pasteHint}</div>
          )}
        </div>
        <button onClick={handleCoverLetterGenerate} disabled={isLoading || !ready} type="button">
          {isLoading ? <div className="spinner"></div> : 'Generate Cover Letter'}
        </button>
      </div>
      {!ready && expanded && (
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
          Paste the job advert or provide a URL to enable generation.
        </div>
      )}
      {!expanded && (
        <div className="job-source-toggle-row">
          <button
            type="button"
            className="link-button"
            onClick={() => setExpanded(true)}
            aria-expanded={expanded}
          >
            Edit job source
          </button>
        </div>
      )}
    </div>
  );
};

export default CoverLetterGen;
